// Shared unit-form pieces used by both CreateUnitDrawer and UnitDetailsDrawer:
// the editable draft shape, the Google-Maps coordinate parser, the geofence
// radius card, the tags multi-select, and the common field block (name ·
// parent/tier · description · location · tags · [code]). The parent/tier/code
// fields differ per mode and are passed in as slots.

import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Chip, Dropdown, Field, Icon, Input, Slider, Switch, Textarea } from '@/components/common';
import { useGetTagsQuery } from '@/api/endpoints/tagsApi';
import {
  RADIUS_DEFAULT,
  RADIUS_MAX,
  RADIUS_MIN,
  SLIDER_STEPS,
  clampRadius,
  formatRadius,
  isCoords,
  radiusToSlider,
  sliderToRadius,
} from './utils/radius';
import type { UnitErrors } from './utils/unitValidation';

/** The drawer's open state, discriminated by mode. */
export type UnitDrawerMode =
  | { type: 'create'; parentId?: string }
  | { type: 'details'; id: string };

/** The editable form draft, shared by create + edit. */
export interface UnitDraft {
  name: string;
  description: string;
  location: string;
  googleMapLink: string;
  coordinates: string;
  /** Geofence radius in meters around the coordinates; null = off. */
  radius: number | null;
  parentId: string;
  tagIds: string[];
}

export const emptyDraft: UnitDraft = {
  name: '',
  description: '',
  location: '',
  googleMapLink: '',
  coordinates: '',
  radius: null,
  parentId: '',
  tagIds: [],
};

// Pull "lat, lng" out of a pasted Google Maps URL (…@24.71,46.67…, ?q=…, or
// the !3d…!4d… place format) — per the design, coordinates are read
// automatically from the link.
export const parseCoords = (url: string): string => {
  const m =
    url.match(/@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/) ??
    url.match(/[?&]q=(-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)/) ??
    url.match(/!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/);
  return m ? `${m[1]}, ${m[2]}` : '';
};

/* ---- radius (geofence around the coordinates) ---- */

interface RadiusFieldProps {
  radius: number | null;
  /** The draft's coordinates — the card is locked until they read as "lat, lng". */
  coordinates: string;
  disabled?: boolean;
  onChange: (radius: number | null) => void;
}

export const RadiusField = ({
  radius,
  coordinates,
  disabled = false,
  onChange,
}: RadiusFieldProps) => {
  const { t } = useTranslation();
  const hasCoords = isCoords(coordinates);
  const on = radius !== null;
  const meters = radius ?? RADIUS_DEFAULT;
  // km/m formatting is for the hint only — the box always reads whole meters.
  const shown = formatRadius(meters);
  const unitLabel = t(shown.unit === 'km' ? 'unit_radius_km' : 'unit_radius_m');
  // Typing buffer: holds the raw keystrokes until blur/Enter so a `1` on the
  // way to `150` isn't clamped to 10 mid-keystroke.
  const [text, setText] = useState<string | null>(null);

  const commit = () => {
    if (text === null) return;
    const typed = Number(text);
    if (text.trim() !== '' && Number.isFinite(typed)) onChange(clampRadius(typed));
    setText(null);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  const hint = !hasCoords ? (
    t('unit_radius_hint_locked')
  ) : on ? (
    <Trans
      i18nKey='unit_radius_covers'
      values={{ value: shown.value, unit: unitLabel }}
      components={{ b: <strong /> }}
    />
  ) : (
    t('unit_radius_hint_optional')
  );

  const classes = [
    'unit-drawer__radius',
    !hasCoords && 'unit-drawer__radius--locked',
    on && 'unit-drawer__radius--on',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <div className='unit-drawer__radius-head'>
        <span className='unit-drawer__radius-title'>{t('unit_field_radius')}</span>
        <Switch
          checked={on}
          disabled={disabled || !hasCoords}
          aria-label={t('unit_field_radius')}
          onChange={(e) => onChange(e.target.checked ? RADIUS_DEFAULT : null)}
        />
      </div>

      <div className='unit-drawer__radius-body'>
        <div className='unit-drawer__radius-track'>
          <Slider
            className='unit-drawer__radius-slider'
            min={0}
            max={SLIDER_STEPS}
            step={1}
            value={radiusToSlider(meters)}
            disabled={disabled || !on}
            aria-label={t('unit_field_radius')}
            onChange={(e) => onChange(sliderToRadius(Number(e.target.value)))}
          />
          {/* bounds are data, not copy — the numbers render literally */}
          <span className='unit-drawer__radius-ends' aria-hidden='true'>
            <span>10 {t('unit_radius_m')}</span>
            <span>500 {t('unit_radius_km')}</span>
          </span>
        </div>

        <span className='unit-drawer__radius-value'>
          {/* min/step pin the browser's step base — without them ↑/↓ stepped
              from the element's stale default value. */}
          <Input
            type='number'
            size='sm'
            inputMode='numeric'
            min={RADIUS_MIN}
            max={RADIUS_MAX}
            step={1}
            className='unit-drawer__radius-input'
            value={text ?? String(meters)}
            disabled={disabled || !on}
            aria-label={t('unit_field_radius')}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={onKeyDown}
          />
          <span className='unit-drawer__radius-unit'>{t('unit_radius_m')}</span>
        </span>

        {/* Static preview per design — tints with the radius, no action. */}
        <span className='unit-drawer__radius-pin' aria-hidden='true'>
          <Icon name='map-pin' size={16} />
        </span>
      </div>

      <p className='unit-drawer__radius-hint'>{hint}</p>
    </div>
  );
};

/* ---- tags multi-select ---- */

interface TagsPickerProps {
  selected: string[];
  disabled?: boolean;
  onToggle: (id: string) => void;
}

export const TagsPicker = ({ selected, disabled = false, onToggle }: TagsPickerProps) => {
  const { t } = useTranslation();
  const { data: tags = [] } = useGetTagsQuery();
  const [open, setOpen] = useState(false);
  const byId = useMemo(() => new Map(tags.map((tag) => [tag.id, tag.title])), [tags]);

  return (
    <div className='unit-drawer__tags'>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='start'
        className='unit-drawer__picker'
        trigger={
          <button
            type='button'
            className='unit-drawer__picker-trigger'
            aria-expanded={open}
            disabled={disabled}
            onClick={() => setOpen((o) => !o)}
          >
            <span className='unit-drawer__picker-value'>
              <Icon name='tag' size={17} className='unit-drawer__picker-icon' />
              <span className='unit-drawer__picker-placeholder'>
                {selected.length
                  ? t('unit_tags_selected', { count: selected.length })
                  : t('unit_tags_placeholder')}
              </span>
            </span>
            <Icon name='chevron-down' size={16} className='unit-drawer__picker-caret' />
          </button>
        }
      >
        <div className='unit-drawer__picker-list'>
          {tags.length === 0 && (
            <span className='unit-drawer__picker-empty'>{t('unit_tags_empty')}</span>
          )}
          {tags.map((tag) => {
            const active = selected.includes(tag.id);
            return (
              <button
                key={tag.id}
                type='button'
                role='menuitemcheckbox'
                aria-checked={active}
                className={`app-dropdown__item${active ? ' is-selected' : ''}`}
                onClick={() => onToggle(tag.id)}
              >
                <Icon name='tag' size={14} />
                <span className='app-dropdown__item-label'>{tag.title}</span>
                {active && <Icon name='check' size={16} />}
              </button>
            );
          })}
        </div>
      </Dropdown>

      {selected.length > 0 && (
        <div className='unit-drawer__tag-chips'>
          {selected.map((id) => (
            <Chip key={id} variant='brand' iconStart='tag'>
              {byId.get(id) ?? id}
              {!disabled && (
                <button
                  type='button'
                  className='unit-drawer__tag-remove'
                  aria-label={t('unit_tag_remove', { tag: byId.get(id) ?? id })}
                  onClick={() => onToggle(id)}
                >
                  <Icon name='x' size={12} />
                </button>
              )}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---- shared field block (name · parent/tier · description · location · tags · [code]) ---- */

interface UnitFieldsProps {
  draft: UnitDraft;
  patch: (changes: Partial<UnitDraft>) => void;
  disabled?: boolean;
  namePlaceholder: string;
  /** Submit-time error codes (from validateUnitDraft) — mapped to copy here. */
  errors?: UnitErrors;
  /** The Field for the parent (create: picker · edit: read-only display). */
  parentField: ReactNode;
  /** The Field for the computed / actual tier. */
  tierField: ReactNode;
  /** Read-only company code Field, edit mode only. */
  codeField?: ReactNode;
}

export const UnitFields = ({
  draft,
  patch,
  disabled = false,
  namePlaceholder,
  errors = {},
  parentField,
  tierField,
  codeField,
}: UnitFieldsProps) => {
  const { t } = useTranslation();
  const nameError = errors.name ? t('unit_name_required') : undefined;
  const coordsError = errors.coordinates ? t('unit_coords_invalid') : undefined;
  const coords = draft.coordinates.trim();
  const hasCoords = isCoords(coords);
  // The radius only means something on real coordinates — losing them turns it off.
  const withCoords = (coordinates: string): Partial<UnitDraft> => ({
    coordinates,
    ...(isCoords(coordinates) ? {} : { radius: null }),
  });

  return (
    <div className='unit-drawer__form'>
      <Field label={t('unit_field_name')} required error={nameError}>
        <Input
          value={draft.name}
          placeholder={namePlaceholder}
          disabled={disabled}
          error={!!nameError}
          onChange={(e) => patch({ name: e.target.value })}
        />
      </Field>

      <div className='unit-drawer__grid'>
        {parentField}
        {tierField}
      </div>

      <Field label={t('unit_field_description')}>
        <Textarea
          rows={2}
          placeholder={t('unit_desc_placeholder')}
          value={draft.description}
          disabled={disabled}
          onChange={(e) => patch({ description: e.target.value })}
        />
      </Field>

      <Field label={t('unit_field_location')}>
        <div className='unit-drawer__location'>
          <Input
            iconStart='map-pin'
            placeholder={t('unit_location_placeholder')}
            value={draft.location}
            disabled={disabled}
            onChange={(e) => patch({ location: e.target.value })}
          />
          <div className='unit-drawer__grid'>
            <Input
              iconStart='link'
              placeholder={t('unit_map_placeholder')}
              value={draft.googleMapLink}
              disabled={disabled}
              onChange={(e) => {
                const googleMapLink = e.target.value;
                const parsed = parseCoords(googleMapLink);
                patch({ googleMapLink, ...(parsed ? withCoords(parsed) : {}) });
              }}
            />
            {/* Unlabeled Field so the message lands under this box, not the block. */}
            <Field error={coordsError}>
              <Input
                iconStart='locate-fixed'
                placeholder={t('unit_coords_placeholder')}
                value={draft.coordinates}
                disabled={disabled}
                error={!!coordsError}
                onChange={(e) => patch(withCoords(e.target.value))}
              />
            </Field>
          </div>
          <RadiusField
            radius={draft.radius}
            coordinates={draft.coordinates}
            disabled={disabled}
            onChange={(radius) => patch({ radius })}
          />
          {hasCoords && (
            <a
              className='unit-drawer__map-link'
              href={
                draft.googleMapLink.trim() ||
                `https://www.google.com/maps?q=${encodeURIComponent(coords)}`
              }
              target='_blank'
              rel='noreferrer'
            >
              <Icon name='external-link' size={15} />
              {t('text_view_on_map')}
            </a>
          )}
          <p className='unit-drawer__note'>
            <Icon name='info' size={14} />
            {t('unit_location_note')}
          </p>
        </div>
      </Field>

      <Field label={t('unit_field_tags')}>
        <TagsPicker
          selected={draft.tagIds}
          disabled={disabled}
          onToggle={(id) =>
            patch({
              tagIds: draft.tagIds.includes(id)
                ? draft.tagIds.filter((x) => x !== id)
                : [...draft.tagIds, id],
            })
          }
        />
      </Field>

      {codeField}
    </div>
  );
};
